import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Check,
  ChevronDown,
  ChevronRight,
  Folder,
  FolderOpen,
  Search,
  Loader2,
} from 'lucide-react';
import { getCategoryTree } from '@/services/categoryService';
import { TreeNode } from '@/types/categories';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import * as Popover from '@radix-ui/react-popover';
import { useDebounce } from '@/hooks/useDebounce';
import { logError } from '@/utils/errorHandler';
import './../../styles/category-tree-select.css';

interface CategoryTreeSelectProps {
  selectedValue?: string | number | null;
  onChange: (nodeId: string | number) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  allowSelectParent?: boolean;
  parentCategory?: number | null;
  onCreate?: (name: string) => Promise<void> | void;
  treeData?: any[]; // Allow passing in already-loaded category data
  displayText?: string; // Override the selected label display with custom text (e.g. breadcrumb)
}

interface TreeNodeRowProps {
  node: TreeNode;
  depth: number;
  selectedValue: string | null;
  onToggle: (node: TreeNode) => void;
  onSelect: (node: TreeNode) => void;
  allowSelectParent?: boolean;
}

const TreeNodeRow: React.FC<TreeNodeRowProps> = ({
  node,
  depth,
  selectedValue,
  onToggle,
  onSelect,
  allowSelectParent = true,
}) => {
  const isSelected = node.value === selectedValue;
  const hasChildren = node.children && node.children.length > 0;

  // Stop propagation for all events
  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <li data-node-label={node.label}>
      <div
        className={`
          flex items-center rounded-md px-2 py-1.5
          ${isSelected ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-foreground' : 'hover:bg-muted dark:hover:bg-muted/60'}
        `}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              onToggle(node);
            }}
            className="mr-1.5 flex items-center justify-center rounded-sm p-0.5 hover:bg-muted dark:hover:bg-muted/60"
            aria-label={node.expanded ? 'Collapse' : 'Expand'}
          >
            <ChevronRight
              className={`size-4 transition-transform ${node.expanded ? 'rotate-90' : ''}`}
            />
          </button>
        ) : (
          <span className="ml-6"></span>
        )}

        <button
          type="button"
          className="flex flex-1 items-center text-left"
          onClick={e => {
            e.stopPropagation();
            onSelect(node);
          }}
          disabled={!allowSelectParent && hasChildren}
          aria-disabled={!allowSelectParent && hasChildren}
        >
          {hasChildren ? (
            node.expanded ? (
              <FolderOpen size={16} className="mr-1.5 opacity-70" />
            ) : (
              <Folder size={16} className="mr-1.5 opacity-70" />
            )
          ) : (
            <Folder size={16} className="mr-1.5 opacity-70" />
          )}
          <span className="truncate">{node.label}</span>
        </button>

        {isSelected && <Check size={16} className="ml-1 text-primary" />}
      </div>

      {hasChildren && node.expanded && (
        <ul className="list-none">
          {node.children?.map(childNode => (
            <TreeNodeRow
              key={childNode.value}
              node={childNode}
              depth={depth + 1}
              selectedValue={selectedValue}
              onToggle={onToggle}
              onSelect={onSelect}
              allowSelectParent={allowSelectParent}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

export const CategoryTreeSelect: React.FC<CategoryTreeSelectProps> = ({
  selectedValue,
  onChange,
  className = '',
  placeholder = 'Select category...',
  disabled = false,
  allowSelectParent = true,
  parentCategory = null,
  onCreate,
  treeData: passedTreeData = [],
  displayText,
}) => {
  const { toast } = useToast();
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [isLoading, setIsLoading] = useState(passedTreeData.length === 0); // Only loading if no data provided
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Selected category display name
  const [selectedLabel, setSelectedLabel] = useState<string>('');

  // Use a ref to check if mounted to avoid memory leaks
  const componentMounted = useRef(true);

  useEffect(() => {
    // Cleanup function
    return () => {
      componentMounted.current = false;
    };
  }, []);

  // Event handler to stop propagation for all mouse and keyboard events
  const stopPropagation = useCallback((e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
  }, []);

  // Load tree data ONCE on component mount - moved before useEffect that uses it
  const loadCategories = useCallback(async () => {
    if (!componentMounted.current) return;

    setIsLoading(true);
    setError(null);
    try {
      const data = await getCategoryTree();
      if (componentMounted.current) {
        setTreeData(data);
      }
    } catch (err) {
      if (componentMounted.current) {
        logError(err as Error, {
          component: 'CategoryTreeSelect',
          action: 'loadCategories',
          metadata: {
            parentCategory,
            hasPassedTreeData: passedTreeData.length > 0,
            componentMounted: componentMounted.current,
          },
        });
        setError('Failed to load categories');
      }
    } finally {
      if (componentMounted.current) {
        setIsLoading(false);
      }
    }
  }, []); // No dependencies - load only once

  // Convert passed Category data to TreeNode format if needed
  useEffect(() => {
    if (passedTreeData && passedTreeData.length > 0) {
      // Check if data is already in TreeNode format or needs conversion
      const firstItem = passedTreeData[0];
      if (firstItem && typeof firstItem === 'object' && 'name' in firstItem) {
        // This is Category data, need to convert
        import('@/services/categoryService').then(({ transformCategoryToTreeNode }) => {
          const converted = passedTreeData.map(transformCategoryToTreeNode);
          setTreeData(converted);
          setIsLoading(false);
        });
      } else if (firstItem && 'label' in firstItem && 'value' in firstItem) {
        // Already TreeNode format
        setTreeData(passedTreeData);
        setIsLoading(false);
      }
    } else {
      // No data passed, need to load from API
      loadCategories();
    }
  }, [passedTreeData, loadCategories]);

  // Handle selectedValue changes separately
  useEffect(() => {
    if (selectedValue && treeData.length > 0) {
      findSelectedLabel(treeData, selectedValue.toString());
    } else if (!selectedValue) {
      setSelectedLabel('');
    }
  }, [selectedValue, treeData]);

  // Separate effect to show toast when error changes
  useEffect(() => {
    if (error) {
      toast({
        title: 'Error',
        description: 'Failed to load category tree',
        variant: 'destructive',
      });
    }
  }, [error]);

  // Find and set the label for the selected value
  const findSelectedLabel = (nodes: TreeNode[], valueToFind: string) => {
    const findNode = (nodeList: TreeNode[]): string | null => {
      for (const node of nodeList) {
        if (node.value === valueToFind) {
          setSelectedLabel(node.label);
          return node.label;
        }
        if (node.children) {
          const result = findNode(node.children);
          if (result) return result;
        }
      }
      return null;
    };

    const result = findNode(nodes);
    return result;
  };

  // Find a leaf node by name (for after create)
  const findLeafByName = (nodes: TreeNode[], nameToFind: string): TreeNode | null => {
    for (const node of nodes) {
      if (node.label.toLowerCase() === nameToFind.toLowerCase()) {
        return node;
      }

      if (node.children && node.children.length > 0) {
        const result = findLeafByName(node.children, nameToFind);
        if (result) return result;
      }
    }
    return null;
  };

  // Toggle node expanded state
  const toggleNode = (targetNode: TreeNode) => {
    const toggleNodeInTree = (nodes: TreeNode[]): TreeNode[] => {
      return nodes.map(node => {
        if (node.value === targetNode.value) {
          // Toggle the expanded state
          return { ...node, expanded: !node.expanded };
        }

        // Recursively process children
        if (node.children && node.children.length > 0) {
          return {
            ...node,
            children: toggleNodeInTree(node.children),
          };
        }

        return node;
      });
    };

    setTreeData(toggleNodeInTree(treeData));
  };

  // Handle node selection
  const handleSelect = (node: TreeNode) => {
    setSelectedLabel(node.label);
    const numericValue = !isNaN(Number(node.value)) ? Number(node.value) : node.value;
    onChange(numericValue);
    setIsOpen(false);
  };

  // Filter tree based on search term
  const filterTreeBySearchTerm = useCallback((nodes: TreeNode[], term: string): TreeNode[] => {
    if (!term.trim()) return nodes;

    const lowerTerm = term.toLowerCase();
    // Deep clone to avoid modifying original data
    const clonedNodes = JSON.parse(JSON.stringify(nodes));

    return clonedNodes.filter((node: TreeNode) => {
      const nodeMatches = node.label.toLowerCase().includes(lowerTerm);
      let childrenMatch = false;

      if (node.children && node.children.length > 0) {
        const matchingChildren = filterTreeBySearchTerm(node.children, term);
        childrenMatch = matchingChildren.length > 0;

        if (childrenMatch) {
          node.children = matchingChildren;
          node.expanded = true; // Auto-expand when matches are found
        }
      }

      return nodeMatches || childrenMatch;
    });
  }, []);

  // Get filtered data based on search term
  const filteredData = React.useMemo(
    () => (debouncedSearchTerm ? filterTreeBySearchTerm(treeData, debouncedSearchTerm) : treeData),
    [debouncedSearchTerm, treeData, filterTreeBySearchTerm]
  );

  // Render loading state
  if (isLoading && !treeData.length) {
    return (
      <div
        className={`relative flex h-10 w-full items-center rounded-md border bg-background px-3 py-2 ${className}`}
      >
        <div className="flex items-center text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" />
          <span>Loading categories...</span>
        </div>
      </div>
    );
  }

  // Render error state
  if (error && !treeData.length) {
    return (
      <div className={`relative w-full rounded-md border px-3 py-2 text-destructive ${className}`}>
        {error}{' '}
        <button className="underline" onClick={loadCategories}>
          Retry
        </button>
      </div>
    );
  }

  // Render main component
  return (
    <div
      className={`relative ${className}`}
      data-component="category-tree-select-container"
      data-testid="category-tree-select"
      onClick={stopPropagation}
      onMouseDown={stopPropagation}
      onMouseUp={stopPropagation}
    >
      <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
        <Popover.Trigger asChild>
          <Button
            variant="outline"
            role="combobox"
            className={`
              h-10 w-full justify-between 
              rounded-lg border-2 border-border/60 
              bg-gradient-to-r from-background to-muted/30
              text-foreground shadow-sm transition-all
              duration-200 ease-in-out hover:border-primary/40
              hover:shadow-md
              ${isOpen ? 'border-primary/60 shadow-md ring-2 ring-primary/20' : ''}
              hover:bg-gradient-to-r hover:from-muted/20 hover:to-muted/40
            `}
            disabled={disabled}
            onClick={e => {
              stopPropagation(e);
            }}
            onMouseDown={stopPropagation}
            aria-expanded={isOpen}
            data-testid="category-select-trigger"
          >
            {displayText || selectedLabel ? (
              <span className="truncate text-foreground">{displayText || selectedLabel}</span>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
            <ChevronDown
              className={`ml-2 size-4 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''} text-muted-foreground`}
            />
          </Button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            side="bottom"
            align="start"
            sideOffset={8}
            collisionPadding={16}
            avoidCollisions
            className="
              z-[9999] flex max-h-[var(--radix-popper-available-height)] min-w-[300px]
              max-w-[90vw] flex-col
              overflow-auto rounded-lg
              border border-border
              bg-card shadow-lg
            "
            style={{
              position: 'relative',
              zIndex: 9999,
              pointerEvents: 'auto',
            }}
            data-testid="category-popover"
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
            onMouseUp={stopPropagation}
            onPointerDownOutside={e => {
              // Prevent closing when clicking inside the popover
              const target = e.target as HTMLElement;
              if (target.closest('[data-component="category-tree-select-container"]')) {
                e.preventDefault();
              }
            }}
          >
            <div className="flex h-full flex-col">
              {/* 1) Sticky search header */}
              <div className="sticky top-0 z-20 shrink-0 border-b border-border bg-card px-3 py-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search categories…"
                    className="w-full pl-10"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    onClick={stopPropagation}
                    onKeyDown={e => {
                      stopPropagation(e);
                      if (e.key === 'Escape') {
                        setSearchTerm('');
                      }
                    }}
                    data-testid="category-search-input"
                  />
                </div>
              </div>

              {/* 2) Tree list fills remaining space */}
              <div className="flex-1 overflow-y-auto overscroll-contain p-2">
                {filteredData.length > 0 ? (
                  <ul className="list-none" data-testid="category-tree">
                    {filteredData.map(node => (
                      <TreeNodeRow
                        key={node.value}
                        node={node}
                        depth={0}
                        selectedValue={selectedValue?.toString() || null}
                        onToggle={toggleNode}
                        onSelect={handleSelect}
                        allowSelectParent={allowSelectParent}
                      />
                    ))}
                  </ul>
                ) : searchTerm ? (
                  <div className="py-4 text-center text-muted-foreground">
                    <p>No categories match "{searchTerm}"</p>
                  </div>
                ) : (
                  <div className="py-4 text-center text-muted-foreground">
                    <p>No categories available</p>
                  </div>
                )}
              </div>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
};
